import { addActor, after, between, budget, rand, spawn } from '../engine';
import { burst, ease, explode, glints, launch, light, rain, shock, wash, type Stage } from '../kit';
import { diamond, drop, glow, glowSprite, gull, ingot, lantern, paper, pixel, twinkle } from '../shapes';
import { rays } from './pieces';
import { TAU, UP, type Preset } from './types';

/* ===================================== Lucky Lever 招财机: lanterns float, gold ingots bounce ===================================== */

const LANTERN = ['#E5484D', '#D93A3F', '#FF6A5C'] as const;
const GOLD = ['#FFD166', '#FFE7A3', '#F2B33D'] as const;

function lanterns(s: Stage, n: number, over: number, size: readonly [number, number]): void {
  rain(s, {
    n,
    over,
    from: 'bottom',
    vy: [-190, -280],
    vx: [-15, 15],
    draw: lantern,
    cols: LANTERN,
    col2: '#FFD166',
    size,
    life: [3, 3.6],
    sway: [12, 26],
    swayF: [1, 2],
    gravity: -18,
    vr: [-0.3, 0.3],
    fadeIn: 0.1,
  });
}

function ingots(s: Stage, x: number, y: number, n: number): void {
  burst(s, x, y, {
    n,
    speed: [320, 560],
    angle: UP,
    spread: 1.1,
    draw: ingot,
    cols: ['#FFD166'],
    col2: '#C8912A',
    size: [14, 18],
    life: [1.3, 1.6],
    gravity: 950,
    drag: 0.8,
    vr: [-4, 4],
  });
}

export const luckyLever: Preset = {
  pal: [...LANTERN, ...GOLD],
  light: '#FFE7A3',
  win(s) {
    burst(s, s.x, s.y, {
      n: 3,
      speed: [60, 120],
      angle: UP,
      spread: 1.2,
      draw: lantern,
      cols: LANTERN,
      col2: '#FFD166',
      size: [9, 12],
      life: [0.85, 1],
      gravity: -180,
      drag: 0.8,
      sway: [10, 20],
    });
    glints(s, s.x, s.y, 10, 30, ['#FFFFFF', ...GOLD]);
    light(s, s.x, s.y, 60, '#FFD166', 0.6, 0.5);
  },
  big(s) {
    lanterns(s, 22, 1400, [16, 24]);
    ingots(s, s.x, s.y, 10);
    rain(s, {
      n: 70,
      over: 1400,
      vy: [60, 140],
      draw: twinkle,
      cols: GOLD,
      size: [3, 6],
      life: [1.6, 2.2],
      vr: [-2, 2],
      add: true,
    });
    light(s, s.x, s.y, 110, '#FFD166', 0.7, 0.5);
  },
  mega(s) {
    rays({
      x: (W) => W / 2,
      y: (H) => H * 0.45,
      r: (W, H) => Math.max(W, H) * 0.5,
      cols: ['#FFD166', '#E5484D'],
      n: 18,
      dur: 3.2,
      alpha: 0.12,
    });
    lanterns(s, 30, 2200, [16, 25]);
    ingots(s, s.W / 2, s.H * 0.55, 12);
    after(700, () => ingots(s, s.W * 0.3, s.H * 0.6, 8));
    after(1000, () => ingots(s, s.W * 0.7, s.H * 0.6, 8));
    for (let i = 0; i < 3; i++) {
      const col = i % 2 ? '#E5484D' : '#FFD166';
      const x = s.W * (0.25 + 0.25 * i);
      launch(
        s,
        x,
        s.H + 10,
        x,
        s.H * 0.25,
        col,
        (ex, ey) => explode(s, ex, ey, { col, n: 80, speed: 380, ring: true }),
        400 + i * 280,
      );
    }
    wash(s.W / 2, s.H * 0.45, { col: '#FFD166', r: Math.max(s.W, s.H) * 0.5, dur: 3, alpha: 0.3 });
  },
};

/* ===================================== Neon Arcade: pixel bursts, scanlines, a glowing grid ===================================== */

const NEON = ['#FF4FD8', '#3BF0FF', '#B6FF4A', '#8B5CFF', '#FFE14A'] as const;

function pixelBurst(s: Stage, x: number, y: number, n: number, speed: number, col?: string): void {
  burst(s, x, y, {
    n,
    speed: [speed * 0.3, speed],
    draw: pixel,
    cols: col ? [col, col, '#FFFFFF'] : NEON,
    size: [2.5, 4],
    life: [0.6, 1],
    drag: 0.3,
    gravity: 60,
    add: true,
    vr: 0,
  });
}

function scanlines(dur: number): void {
  addActor({
    dur,
    front: true,
    draw(g, k, f) {
      g.globalCompositeOperation = 'lighter';
      for (let i = 0; i < 3; i++) {
        const y = ((k * 1.3 - i * 0.18) % 1.2) * f.H;
        const col = NEON[i] ?? '#3BF0FF';
        g.globalAlpha = 0.18 * ease.env(k, 0.1, 0.8);
        g.fillStyle = col;
        g.fillRect(0, y - 10, f.W, 20);
        g.globalAlpha = 0.6 * ease.env(k, 0.1, 0.8);
        g.fillRect(0, y - 1, f.W, 2);
      }
    },
  });
}

function neonGrid(dur: number): void {
  addActor({
    dur,
    draw(g, k, f) {
      const a = ease.env(k, 0.15, 0.75);
      const hz = f.H * 0.62;
      g.globalCompositeOperation = 'lighter';
      g.strokeStyle = '#FF4FD8';
      g.lineWidth = 2;
      g.globalAlpha = 0.5 * a;
      g.beginPath();
      g.moveTo(0, hz);
      g.lineTo(f.W, hz);
      g.stroke();
      g.strokeStyle = '#3BF0FF';
      g.globalAlpha = 0.28 * a;
      const vx = f.W / 2;
      for (let i = -12; i <= 12; i++) {
        g.beginPath();
        g.moveTo(vx + i * 18, hz);
        g.lineTo(vx + i * f.W * 0.14, f.H);
        g.stroke();
      }
      const off = (f.t * 0.9) % 1;
      for (let j = 0; j < 9; j++) {
        const q = (j + off) / 9;
        const y = hz + (f.H - hz) * q * q;
        g.beginPath();
        g.moveTo(0, y);
        g.lineTo(f.W, y);
        g.stroke();
      }
      const sp = glowSprite('#FF4FD8');
      if (sp) {
        g.globalAlpha = 0.35 * a;
        g.drawImage(sp, vx - f.W * 0.3, hz - f.H * 0.25, f.W * 0.6, f.H * 0.5);
      }
    },
  });
}

export const neonArcade: Preset = {
  pal: NEON,
  light: '#FFFFFF',
  win(s) {
    pixelBurst(s, s.x, s.y, 36, 320);
    burst(s, s.x, s.y, {
      n: 6,
      speed: [60, 160],
      draw: diamond,
      cols: NEON,
      size: [5, 8],
      life: [0.6, 0.9],
      drag: 0.4,
      vr: [-4, 4],
      add: true,
    });
  },
  big(s) {
    scanlines(1.6);
    for (let i = 0; i < 6; i++) {
      const col = NEON[i % NEON.length] ?? '#3BF0FF';
      after(i * 220, () => {
        const x = s.W * between(0.15, 0.85);
        const y = s.H * between(0.2, 0.5);
        pixelBurst(s, x, y, 60, 420, col);
        light(s, x, y, 70, col, 0.4, 0.7);
      });
    }
    rain(s, {
      n: 40,
      over: 1200,
      vy: [80, 160],
      draw: diamond,
      cols: NEON,
      size: [5, 9],
      life: [1.8, 2.2],
      vr: [-3, 3],
      add: true,
    });
    pixelBurst(s, s.x, s.y, 40, 360);
  },
  mega(s) {
    neonGrid(3.4);
    scanlines(2);
    after(300, () => {
      pixelBurst(s, s.W / 2, s.H * 0.4, 200, 720);
      shock(s.W / 2, s.H * 0.4, { col: '#3BF0FF', r: Math.max(s.W, s.H) * 0.45, dur: 1, width: 4 });
      shock(s.W / 2, s.H * 0.4, {
        col: '#FF4FD8',
        r: Math.max(s.W, s.H) * 0.35,
        dur: 1.1,
        width: 4,
        delay: 0.1,
      });
    });
    for (let i = 0; i < 5; i++) {
      const col = NEON[i] ?? '#3BF0FF';
      after(900 + i * 260, () =>
        pixelBurst(s, s.W * (0.15 + 0.175 * i), s.H * between(0.15, 0.35), 60, 420, col),
      );
    }
  },
};

/* ===================================== Pirate Bay / Cove: confetti, seagulls, sea spray, a rolling wave ===================================== */

const SEA = ['#2EC4B6', '#F4D58D', '#E5484D', '#2B4C7E', '#FFFFFF'] as const;
const SPRAY = ['#BFEFFF', '#E8F6FF', '#8FE3FF'] as const;

function gulls(s: Stage, n: number, delay = 0): void {
  for (let i = 0; i < budget(n); i++) {
    const left = i % 2 === 0;
    spawn({
      x: left ? -40 : s.W + 40,
      y: s.H * between(0.12, 0.35),
      vx: (left ? 1 : -1) * between(220, 300),
      vy: between(-20, 10),
      delay: delay + i * 0.25,
      draw: gull,
      col: s.ink,
      size: between(12, 17),
      life: 4.5,
      swayY: 20,
      swayF: 2,
    });
  }
}

function seaSpray(s: Stage, x: number, n: number): void {
  burst(s, x, s.H + 6, {
    n,
    speed: [320, 620],
    angle: UP,
    spread: 0.7,
    draw: drop,
    cols: SPRAY,
    size: [2.5, 5],
    life: [0.9, 1.2],
    gravity: 900,
    drag: 0.8,
  });
}

function wave(): void {
  let acc = 0;
  addActor({
    dur: 3,
    draw(g, k, f) {
      const rise = Math.sin(Math.min(1, k) * Math.PI);
      const top = f.H - f.H * 0.26 * rise;
      g.globalAlpha = 0.42;
      g.fillStyle = '#2B6FB8';
      g.beginPath();
      g.moveTo(0, f.H);
      for (let x = 0; x <= f.W; x += 12) g.lineTo(x, top + Math.sin(x * 0.012 + f.t * 3) * 18 * rise);
      g.lineTo(f.W, f.H);
      g.closePath();
      g.fill();
      g.globalAlpha = 0.32;
      g.fillStyle = '#2EC4B6';
      g.beginPath();
      g.moveTo(0, f.H);
      for (let x = 0; x <= f.W; x += 12) g.lineTo(x, top + 26 + Math.sin(x * 0.016 - f.t * 2.4) * 14 * rise);
      g.lineTo(f.W, f.H);
      g.closePath();
      g.fill();
      // foam on the crest
      g.globalAlpha = 0.7 * rise;
      g.fillStyle = '#FFFFFF';
      for (let x = 6; x <= f.W; x += 22) {
        const y = top + Math.sin(x * 0.012 + f.t * 3) * 18 * rise;
        g.beginPath();
        g.arc(x, y, 3 + Math.abs(Math.sin(x + f.t * 4)) * 4, 0, TAU);
        g.fill();
      }
      if (k < 0.8) {
        acc += f.dt * 30 * rise;
        while (acc >= 1) {
          acc -= 1;
          const x = rand() * f.W;
          const y = top + Math.sin(x * 0.012 + f.t * 3) * 18 * rise;
          spawn({
            x,
            y,
            vx: between(-60, 60),
            vy: -between(160, 360),
            draw: drop,
            col: SPRAY[Math.floor(rand() * 3)] ?? '#BFEFFF',
            size: between(2.5, 5),
            life: 1,
            gravity: 900,
          });
        }
      }
    },
  });
}

export const pirate: Preset = {
  pal: SEA,
  light: '#FFFFFF',
  win(s) {
    burst(s, s.x, s.y, {
      n: 28,
      speed: [180, 400],
      angle: UP,
      spread: 1.6,
      draw: paper,
      size: [4.5, 7],
      life: [0.75, 0.95],
      gravity: 640,
      drag: 0.5,
      flip: [6, 11],
      vr: [-5, 5],
    });
    burst(s, s.x, s.y, {
      n: 12,
      speed: [200, 360],
      angle: UP,
      spread: 1,
      draw: drop,
      cols: SPRAY,
      size: [2.5, 4],
      life: [0.7, 0.9],
      gravity: 900,
      drag: 0.8,
    });
  },
  big(s) {
    rain(s, {
      n: 160,
      over: 1300,
      vy: [120, 240],
      draw: paper,
      size: [5, 8],
      life: [2, 2.4],
      sway: [15, 30],
      flip: [5, 10],
      vr: [-3, 3],
      gravity: 80,
    });
    gulls(s, 5);
    for (let i = 0; i < 4; i++) after(i * 300, () => seaSpray(s, s.W * between(0.1, 0.9), 30));
    burst(s, s.x, s.y, {
      n: 30,
      speed: [200, 420],
      angle: UP,
      spread: 1.6,
      draw: paper,
      size: [5, 8],
      life: [1, 1.3],
      gravity: 600,
      drag: 0.5,
      flip: [6, 11],
      vr: [-5, 5],
    });
  },
  mega(s) {
    wave();
    gulls(s, 7, 0.2);
    rain(s, {
      n: 170,
      over: 2200,
      vy: [120, 240],
      draw: paper,
      size: [5, 8],
      life: [2, 2.4],
      sway: [15, 30],
      flip: [5, 10],
      vr: [-3, 3],
      gravity: 80,
    });
    for (let i = 0; i < 3; i++) {
      const col = SEA[i] ?? '#2EC4B6';
      const x = s.W * (0.25 + 0.25 * i);
      launch(
        s,
        x,
        s.H * 0.8,
        x,
        s.H * 0.25,
        col,
        (ex, ey) => explode(s, ex, ey, { col, n: 70, speed: 360 }),
        700 + i * 300,
      );
    }
  },
};

/* ===================================== the jackpot 乐游: gold and the house colours, above mega ===================================== */

const JP = ['#FFD166', '#FFE7A3', '#F2B33D', '#2EE6C9', '#FF8A3D'] as const;

export const jackpot: Preset = {
  pal: JP,
  light: '#FFF1C2',
  win(s) {
    jackpot.mega(s);
  },
  big(s) {
    jackpot.mega(s);
  },
  mega(s) {
    const cx = s.W / 2;
    const cy = s.H * 0.42;
    const M = Math.max(s.W, s.H);
    rays({
      x: (W) => W / 2,
      y: (H) => H * 0.42,
      r: (W, H) => Math.max(W, H) * 0.6,
      cols: ['#FFD166', '#2EE6C9', '#FFD166', '#FF8A3D'],
      n: 24,
      dur: 4.8,
      alpha: 0.16,
      spin: 0.25,
    });
    wash(cx, cy, { col: '#FFD166', r: M * 0.6, dur: 1.4, alpha: 0.7 });
    for (const [i, col] of ['#FFF1C2', '#FFD166', '#2EE6C9'].entries())
      shock(cx, cy, { col, r: M * (0.4 + i * 0.12), dur: 1.3, width: 7 - i, delay: i * 0.12 });
    const st: Stage = { ...s, x: cx, y: cy };
    burst(st, cx, cy, {
      n: 90,
      speed: [200, 620],
      draw: [twinkle, glow],
      cols: JP,
      size: [3, 8],
      life: [1.2, 1.8],
      drag: 0.45,
      gravity: 90,
      vr: [-3, 3],
      add: true,
    });
    for (let i = 0; i < 8; i++) {
      const col = JP[i % JP.length] ?? '#FFD166';
      const x = s.W * (0.12 + 0.76 * ((i * 0.618 + 0.1) % 1));
      launch(
        st,
        x,
        s.H + 10,
        x + between(-40, 40),
        s.H * between(0.14, 0.38),
        col,
        (ex, ey) => explode(st, ex, ey, { col, n: 70, speed: 380, ring: i % 3 === 0 }),
        300 + i * 300,
      );
    }
    rain(st, {
      n: 220,
      over: 2600,
      vy: [120, 230],
      draw: paper,
      cols: ['#FFD166', '#F2B33D', '#FFE7A3', '#2EE6C9', '#FF8A3D'],
      size: [4, 7],
      life: [2.2, 2.8],
      sway: [15, 35],
      flip: [5, 10],
      vr: [-3, 3],
      gravity: 70,
    });
    after(3000, () => {
      explode(st, cx, cy, { col: '#FFD166', n: 150, speed: 600, ring: true });
      after(150, () => explode(st, cx, cy, { col: '#2EE6C9', n: 90, speed: 420, ring: true }));
      wash(cx, cy, { col: '#FFD166', r: M * 0.5, dur: 1.6, alpha: 0.5 });
      after(400, () =>
        rain(st, {
          n: 100,
          over: 800,
          vy: [30, 90],
          draw: twinkle,
          cols: ['#FFFFFF', '#FFD166', '#FFE7A3'],
          size: [2, 4],
          life: [1.2, 1.6],
          gravity: 60,
          add: true,
          vr: [-4, 4],
        }),
      );
    });
  },
};
